/**
 * Hotel Search Component
 * Handles hotel search form functionality and validation
 */

import { HotelSearchData } from '../types/data-models';
import { validateRequired, validateDate, validateFutureDate } from '../utils/simple-validation';

export class HotelSearchComponent {
  private form: HTMLFormElement;
  private destinationInput!: HTMLInputElement;
  private checkinDateInput!: HTMLInputElement;
  private checkoutDateInput!: HTMLInputElement;
  private guestsSelect!: HTMLSelectElement;
  private roomsSelect!: HTMLSelectElement;
  private submitButton!: HTMLButtonElement;
  private generalErrorDiv!: HTMLElement;

  public onSearch: ((data: HotelSearchData) => void) | null = null;

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.initializeElements();
    this.bindEvents();
    this.setMinDates();
  }

  private initializeElements(): void {
    this.destinationInput = this.form.querySelector('#destination') as HTMLInputElement;
    this.checkinDateInput = this.form.querySelector('#checkin-date') as HTMLInputElement;
    this.checkoutDateInput = this.form.querySelector('#checkout-date') as HTMLInputElement;
    this.guestsSelect = this.form.querySelector('#guests') as HTMLSelectElement;
    this.roomsSelect = this.form.querySelector('#rooms') as HTMLSelectElement;
    this.submitButton = this.form.querySelector('.hotel-search-form__submit') as HTMLButtonElement;
    this.generalErrorDiv = this.form.querySelector('.hotel-search-form__general-error') as HTMLElement;
  }

  private bindEvents(): void {
    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Real-time validation
    this.destinationInput.addEventListener('blur', () => this.validateField('destination'));
    this.checkinDateInput.addEventListener('change', () => {
      this.validateField('checkinDate');
      this.updateCheckoutDateMin();
    });
    this.checkoutDateInput.addEventListener('change', () => this.validateField('checkoutDate'));
    this.guestsSelect.addEventListener('change', () => this.validateField('guests'));
    this.roomsSelect.addEventListener('change', () => this.validateField('rooms'));

    // Clear errors on input
    [this.destinationInput, this.checkinDateInput, this.checkoutDateInput, this.guestsSelect, this.roomsSelect]
      .forEach(input => {
        input.addEventListener('input', () => this.clearFieldError(input.name));
      });
  }

  private setMinDates(): void {
    const todayParts = new Date().toISOString().split('T');
    const today = todayParts[0] || '';
    if (this.checkinDateInput) {
      this.checkinDateInput.min = today;
    }
    if (this.checkoutDateInput) {
      this.checkoutDateInput.min = today;
    }
  }

  private updateCheckoutDateMin(): void {
    if (this.checkinDateInput.value) {
      const checkinDate = new Date(this.checkinDateInput.value);
      const nextDay = new Date(checkinDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      const nextDayParts = nextDay.toISOString().split('T');
      this.checkoutDateInput.min = nextDayParts[0] || '';
      
      // Clear checkout date if it's not after checkin date
      if (this.checkoutDateInput.value && this.checkoutDateInput.value <= this.checkinDateInput.value) {
        this.checkoutDateInput.value = '';
      }
    }
  }

  private validateField(fieldName: string): boolean {
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'destination':
        if (!validateRequired(this.destinationInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une destination';
        } else if (this.destinationInput.value.length < 2) {
          isValid = false;
          errorMessage = 'La destination doit contenir au moins 2 caractères';
        }
        break;

      case 'checkinDate':
        if (!validateRequired(this.checkinDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner une date d\'arrivée';
        } else if (!validateDate(this.checkinDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une date valide';
        } else if (!validateFutureDate(this.checkinDateInput.value)) {
          isValid = false;
          errorMessage = 'La date d\'arrivée doit être dans le futur';
        }
        break;

      case 'checkoutDate':
        if (!validateRequired(this.checkoutDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner une date de départ';
        } else if (!validateDate(this.checkoutDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une date de départ valide';
        } else if (this.checkinDateInput.value && this.checkoutDateInput.value <= this.checkinDateInput.value) {
          isValid = false;
          errorMessage = 'La date de départ doit être après la date d\'arrivée';
        }
        break;

      case 'guests':
        if (!validateRequired(this.guestsSelect.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner le nombre de voyageurs';
        } else {
          const guests = parseInt(this.guestsSelect.value);
          if (guests < 1 || guests > 8) {
            isValid = false;
            errorMessage = 'Le nombre de voyageurs doit être entre 1 et 8';
          }
        }
        break;

      case 'rooms':
        if (!validateRequired(this.roomsSelect.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner le nombre de chambres';
        } else {
          const rooms = parseInt(this.roomsSelect.value);
          if (rooms < 1 || rooms > 5) {
            isValid = false;
            errorMessage = 'Le nombre de chambres doit être entre 1 et 5';
          }
        }
        break;
    }

    this.displayFieldError(fieldName, isValid ? '' : errorMessage);
    return isValid;
  }

  private displayFieldError(fieldName: string, message: string): void {
    const errorElementId = `${fieldName.replace(/([A-Z])/g, '-$1').toLowerCase()}-error`;
    const errorElement = document.getElementById(errorElementId);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = message ? 'block' : 'none';
    }

    // Update field styling
    const field = this.form.querySelector(`[name="${fieldName}"]`) as HTMLElement;
    if (field) {
      if (message) {
        field.classList.add('form-input--error');
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.classList.remove('form-input--error');
        field.setAttribute('aria-invalid', 'false');
      }
    }
  }

  private clearFieldError(fieldName: string): void {
    this.displayFieldError(fieldName, '');
  }

  private validateForm(): boolean {
    const fields = ['destination', 'checkinDate', 'checkoutDate', 'guests', 'rooms'];
    let isValid = true;

    fields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  }

  private displayGeneralError(message: string): void {
    if (this.generalErrorDiv) {
      this.generalErrorDiv.textContent = message;
      this.generalErrorDiv.style.display = message ? 'block' : 'none';
    }
  }

  private clearGeneralError(): void {
    this.displayGeneralError('');
  }

  private setLoadingState(loading: boolean): void {
    const submitText = this.submitButton.querySelector('.hotel-search-form__submit-text') as HTMLElement;
    const loadingText = this.submitButton.querySelector('.hotel-search-form__submit-loading') as HTMLElement;

    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('btn--loading');
      if (submitText) submitText.style.display = 'none';
      if (loadingText) loadingText.style.display = 'inline';
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('btn--loading');
      if (submitText) submitText.style.display = 'inline';
      if (loadingText) loadingText.style.display = 'none';
    }
  }

  private async handleSubmit(): Promise<void> {
    this.clearGeneralError();

    if (!this.validateForm()) {
      this.displayGeneralError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    const searchData: HotelSearchData = {
      destination: this.destinationInput.value.trim(),
      checkinDate: new Date(this.checkinDateInput.value),
      checkoutDate: new Date(this.checkoutDateInput.value),
      guests: parseInt(this.guestsSelect.value),
      rooms: parseInt(this.roomsSelect.value)
    };

    this.setLoadingState(true);

    try {
      if (this.onSearch) {
        await this.onSearch(searchData);
      }
    } catch (error) {
      console.error('Search error:', error);
      this.displayGeneralError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    } finally {
      this.setLoadingState(false);
    }
  }

  public populateForm(data: HotelSearchData): void {
    if (this.destinationInput) this.destinationInput.value = data.destination;
    if (this.checkinDateInput) {
      const checkinParts = data.checkinDate.toISOString().split('T');
      this.checkinDateInput.value = checkinParts[0] || '';
    }
    if (this.checkoutDateInput) {
      const checkoutParts = data.checkoutDate.toISOString().split('T');
      this.checkoutDateInput.value = checkoutParts[0] || '';
    }
    if (this.guestsSelect) {
      this.guestsSelect.value = data.guests.toString();
    }
    if (this.roomsSelect) {
      this.roomsSelect.value = data.rooms.toString();
    }
    
    // Update checkout date minimum
    this.updateCheckoutDateMin();
  }

  public getFormData(): HotelSearchData {
    return {
      destination: this.destinationInput.value.trim(),
      checkinDate: new Date(this.checkinDateInput.value),
      checkoutDate: new Date(this.checkoutDateInput.value),
      guests: parseInt(this.guestsSelect.value),
      rooms: parseInt(this.roomsSelect.value)
    };
  }

  public reset(): void {
    this.form.reset();
    this.clearGeneralError();
    
    // Clear all field errors
    ['destination', 'checkinDate', 'checkoutDate', 'guests', 'rooms'].forEach(field => {
      this.clearFieldError(field);
    });
    
    // Reset min dates
    this.setMinDates();
  }
}